import React from 'react'
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip
} from '@mui/material'
import { useTranslation } from 'react-i18next'

interface MonthlyStats {
  requests: number
  minutes: number
  cost: number
}

interface UsageOverviewTabProps {
  thisMonth: MonthlyStats
  lastMonth: MonthlyStats
  formatTime: (minutes: number) => string
  formatTrend: (current: number, previous: number) => {
    label: string
    direction: 'up' | 'down' | 'flat'
    color: string
  }
}

const UsageOverviewTab: React.FC<UsageOverviewTabProps> = ({
  thisMonth,
  lastMonth,
  formatTime,
  formatTrend
}) => {
  const { t } = useTranslation()

  const requestsTrend = formatTrend(thisMonth.requests, lastMonth.requests)
  const minutesTrend = formatTrend(thisMonth.minutes, lastMonth.minutes)
  const costTrend = formatTrend(thisMonth.cost, lastMonth.cost)

  // For cost, up is bad (error), down is good (success)
  const costChipColor =
    costTrend.direction === 'up'
      ? 'error'
      : costTrend.direction === 'down'
      ? 'success'
      : 'default'

  return (
    <Card
      sx={{
        mb: 3,
        background:
          'linear-gradient(135deg, rgba(0, 207, 243, 0.02), rgba(0, 207, 243, 0.05))'
      }}
    >
      <CardContent>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontWeight: 700, color: '#1a237e' }}
        >
          {t('monthlyComparison', 'Monthly Usage Comparison')}
        </Typography>
        <Grid container spacing={3}>
          {/* Requests */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="textSecondary">
              {t('requests', 'Requests')}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {thisMonth.requests.toLocaleString()}
              </Typography>
              <Chip
                size="small"
                label={requestsTrend.label}
                color={
                  requestsTrend.direction === 'up'
                    ? 'success'
                    : requestsTrend.direction === 'down'
                    ? 'error'
                    : 'default'
                }
              />
            </Box>
            <Typography variant="caption" color="textSecondary">
              vs {lastMonth.requests.toLocaleString()} {t('lastMonth', 'last month')}
            </Typography>
          </Grid>

          {/* Minutes */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="textSecondary">
              {t('minutes', 'Minutes')}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {formatTime(thisMonth.minutes)}
              </Typography>
              <Chip
                size="small"
                label={minutesTrend.label}
                color={
                  minutesTrend.direction === 'up'
                    ? 'success'
                    : minutesTrend.direction === 'down'
                    ? 'error'
                    : 'default'
                }
              />
            </Box>
            <Typography variant="caption" color="textSecondary">
              vs {formatTime(lastMonth.minutes)} {t('lastMonth', 'last month')}
            </Typography>
          </Grid>

          {/* Cost */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="textSecondary">
              {t('cost', 'Cost')}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                €{(thisMonth.cost || 0).toFixed(2)}
              </Typography>
              <Chip size="small" label={costTrend.label} color={costChipColor} />
            </Box>
            <Typography variant="caption" color="textSecondary">
              vs €{(lastMonth.cost || 0).toFixed(2)} {t('lastMonth', 'last month')}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

export default UsageOverviewTab
